import { useState, useEffect, type FormEvent } from 'react';
import { authHeaders } from '../../utils/auth';
import { apiFetch } from '../../utils/api';
import type { BatchOrigin } from '../../types';

type OriginalBatch = { id: number; galleryName: string; year: string };
type AsMember     = { id: number; fName: string; lName: string };
type Group        = { id: number; name: string };

type Props = {
  selectedBatchIds: Set<number>;
  onWindowOpened: () => void;
  onCancel: () => void;
};

export default function ReviewGalleryOpenForm({ selectedBatchIds, onWindowOpened, onCancel }: Props) {
  const [originalBatches, setOriginalBatches] = useState<OriginalBatch[]>([]);
  const [asMembers, setAsMembers]             = useState<AsMember[]>([]);
  const [allGroups, setAllGroups]             = useState<Group[]>([]);
  const [origins, setOrigins]                 = useState<BatchOrigin[]>([]);
  const [notifyGroupIds, setNotifyGroupIds]   = useState<Set<number>>(new Set());
  const [assignedAdId, setAssignedAdId]       = useState('');
  const [batchOriginId, setBatchOriginId]     = useState('');
  const [batchName, setBatchName]             = useState('');
  const [batchYear, setBatchYear]             = useState('');
  const [opening, setOpening]                 = useState(false);
  const [openError, setOpenError]             = useState('');

  useEffect(() => {
    Promise.all([
      apiFetch('/api/batches', { headers: authHeaders() }).then(r => r.json()),
      apiFetch('/api/users?role=artistic_director', { headers: authHeaders() }).then(r => r.json()),
      apiFetch('/api/groups', { headers: authHeaders() }).then(r => r.json()),
      apiFetch('/api/batch-origins', { headers: authHeaders() }).then(r => r.json()),
    ]).then(([allBatches, adUsers, groupList, originList]: [unknown, unknown, unknown, unknown]) => {
      const bs = Array.isArray(allBatches) ? allBatches : [];
      setOriginalBatches(
        bs
          .filter((b: { batchType: string }) => b.batchType === 'original')
          .map((b: { id: number; galleryName: string; year: string }) => ({
            id: b.id, galleryName: b.galleryName, year: b.year,
          }))
      );
      setAsMembers(Array.isArray(adUsers) ? adUsers : []);
      setAllGroups(
        Array.isArray(groupList)
          ? (groupList as { id: number; name: string }[]).map(g => ({ id: g.id, name: g.name }))
          : []
      );
      setOrigins(Array.isArray(originList) ? originList : []);
    }).catch(() => {});
  }, []);

  // Auto-fill name and season when exactly one batch is selected
  useEffect(() => {
    if (selectedBatchIds.size === 1) {
      const id = [...selectedBatchIds][0];
      const batch = originalBatches.find(b => b.id === id);
      if (batch) {
        setBatchName(batch.galleryName);
        setBatchYear(batch.year);
      }
    } else if (selectedBatchIds.size === 0) {
      setBatchName('');
      setBatchYear('');
    }
  }, [selectedBatchIds, originalBatches]);

  const handleOpen = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedBatchIds.size === 0) { setOpenError('Select at least one batch above.'); return; }
    if (selectedBatchIds.size > 1 && !batchName.trim()) {
      setOpenError('A gallery name is required when opening across multiple batches.');
      return;
    }
    if (selectedBatchIds.size > 1 && !batchYear.trim()) {
      setOpenError('A year is required when opening across multiple batches.');
      return;
    }
    setOpening(true);
    setOpenError('');
    try {
      const res = await apiFetch('/api/batches/review-gallery/open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          sourceBatchIds:  [...selectedBatchIds],
          assignedAdId:    parseInt(assignedAdId, 10),
          batchName:       batchName.trim(),
          batchYear:       batchYear.trim(),
          notifyGroupIds:  [...notifyGroupIds],
          batchOriginId:   batchOriginId ? parseInt(batchOriginId, 10) : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setOpenError((err as { message?: string }).message || 'Failed to open review window');
        return;
      }
      onWindowOpened();
    } catch {
      setOpenError('Network error — failed to open review window.');
    } finally {
      setOpening(false);
    }
  };

  const isMulti = selectedBatchIds.size > 1;
  const required = <span style={{ color: 'var(--color-error)', marginLeft: 2 }}>*</span>;

  const submitLabel = opening
    ? 'Opening…'
    : selectedBatchIds.size > 0
      ? `Open Review Window (${selectedBatchIds.size} batch${selectedBatchIds.size !== 1 ? 'es' : ''})`
      : 'Open Review Window';

  // 0 is the "All Artists" sentinel — selecting it clears specific groups and vice versa.
  const ALL_ARTISTS_ID = 0;
  const toggleGroup = (id: number) => {
    setNotifyGroupIds(prev => {
      const next = new Set(prev);
      if (id === ALL_ARTISTS_ID) {
        return next.has(ALL_ARTISTS_ID) ? new Set() : new Set([ALL_ARTISTS_ID]);
      }
      // selecting a specific group deselects "All Artists"
      next.delete(ALL_ARTISTS_ID);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const isDisabled =
    opening ||
    selectedBatchIds.size === 0 ||
    !assignedAdId ||
    notifyGroupIds.size === 0 ||
    (isMulti && !batchName.trim()) ||
    (isMulti && !batchYear.trim());

  return (
    <form className='user-form' onSubmit={handleOpen}>
      <h3 className='user-form-title'>Release Full Gallery</h3>
      {openError && <p className='user-form-error'>{openError}</p>}
      <div className='user-form-row'>
        <div className='user-form-field'>
          <label>
            Gallery Name
            {isMulti && required}
          </label>
          <input
            type='text'
            value={batchName}
            onChange={e => setBatchName(e.target.value)}
            placeholder={
              selectedBatchIds.size === 1
                ? 'Auto-filled from production name'
                : selectedBatchIds.size === 0
                  ? 'Select a batch below first'
                  : 'Enter a name for this gallery'
            }
          />
        </div>
        <div className='user-form-field'>
          <label>
            Year
            {isMulti && required}
          </label>
          <input
            type='text'
            value={batchYear}
            onChange={e => setBatchYear(e.target.value)}
            maxLength={4}
            placeholder={
              selectedBatchIds.size === 1
                ? 'Auto-filled from batch year'
                : selectedBatchIds.size === 0
                  ? 'Select a batch below first'
                  : 'Enter a year for this gallery'
            }
          />
        </div>
        <div className='user-form-field'>
          <label>Artistic Staff <span style={{ fontWeight: 400, color: '#888' }}>(required)</span></label>
          <select value={assignedAdId} onChange={e => setAssignedAdId(e.target.value)}>
            <option value=''>— Select Artistic Staff —</option>
            {asMembers.map(u => (
              <option key={u.id} value={String(u.id)}>
                {u.fName} {u.lName}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className='user-form-row' style={{ marginTop: '0.75rem' }}>
        <div className='user-form-field'>
          <label>
            Batch Origin
            <span className='field-label-hint'>(source label for exported filenames)</span>
          </label>
          <select value={batchOriginId} onChange={e => setBatchOriginId(e.target.value)}>
            <option value=''>— None —</option>
            {origins.filter(o => o.isActive).map(o => (
              <option key={o.id} value={String(o.id)}>{o.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className='user-form-field' style={{ marginTop: '0.75rem' }}>
        <label>
          Notify Groups {required}
          <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
            — artists in these groups will receive the review window email
          </span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.35rem' }}>
          {allGroups.map(g => {
            const active = notifyGroupIds.has(g.id);
            return (
              <button
                key={g.id}
                type='button'
                onClick={() => toggleGroup(g.id)}
                style={{
                  padding: '0.3rem 0.85rem',
                  borderRadius: '9999px',
                  border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-surface)' : 'var(--color-text-muted)',
                  fontSize: '0.8rem',
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                }}
              >
                {g.name}
              </button>
            );
          })}
          {(() => {
            const active = notifyGroupIds.has(ALL_ARTISTS_ID);
            return (
              <button
                type='button'
                onClick={() => toggleGroup(ALL_ARTISTS_ID)}
                style={{
                  padding: '0.3rem 0.85rem',
                  borderRadius: '9999px',
                  border: `1.5px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
                  background: active ? 'var(--color-primary)' : 'transparent',
                  color: active ? 'var(--color-surface)' : 'var(--color-text-muted)',
                  fontSize: '0.8rem',
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                }}
              >
                All Artists
              </button>
            );
          })()}
        </div>
      </div>
      <div className='user-form-actions'>
        <button type='button' className='user-cancel-btn' onClick={onCancel} disabled={opening}>
          Cancel
        </button>
        <button type='submit' className='user-submit-btn' disabled={isDisabled}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
